import { useState, useEffect, useRef, useCallback } from "react";
import Modal from "../../layouts/Modal";
import { gtWidgetURL } from "../../config";

const DEFAULT_MODEL = "CI00601";

interface CategoryDiagramModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ucid: string;
  ucidZone: string;
  onSave: (zone: string, partCode: string | null) => void;
}

function buildIframeSrc(model: string, zone: string | null) {
  const params = new URLSearchParams({ model });

  if (zone) {
    params.set("mode", "vehicle");
    params.set("zone", zone);
  } else {
    params.set("mode", "boardnav");
  }

  return `${gtWidgetURL}/embed.html?${params.toString()}`;
}

export default function CategoryDiagramModal({
  open,
  setOpen,
  ucid,
  ucidZone,
  onSave,
}: CategoryDiagramModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [model, setModel] = useState(DEFAULT_MODEL);
  const [selectedZone, setSelectedZone] = useState<string | null>(ucidZone || null);
  const [clickedPart, setClickedPart] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedZone(ucidZone || null);
      setClickedPart(null);
      setSaved(false);
    }
  }, [open, ucidZone]);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      if (e.origin !== gtWidgetURL) return;
      const data = e.data;
      if (!data || !data.type) return;

      if (data.type === "zoneSelected") {
        setSelectedZone(data.zoneId);
        setClickedPart(null);
        setSaved(false);

        iframeRef.current?.contentWindow?.postMessage(
          { type: "navigate", mode: "vehicle", model, zone: data.zoneId },
          gtWidgetURL
        );
      } else if (data.type === "partClicked") {
        setClickedPart(data.partCode);
        setSaved(false);
      }
    },
    [model]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, handleMessage]);

  const handleBack = () => {
    setSelectedZone(null);
    setClickedPart(null);

    iframeRef.current?.contentWindow?.postMessage(
      { type: "navigate", mode: "boardnav", model },
      gtWidgetURL
    );
  };

  const handleSave = () => {
    if (!selectedZone) return;
    onSave(selectedZone, clickedPart);
    setSaved(true);
  };

  const handleModelChange = (newModel: string) => {
    setModel(newModel);
    setSelectedZone(null);
    setClickedPart(null);
    setSaved(false);
  };

  const iframeSrc = open ? buildIframeSrc(model, selectedZone) : "";
  const canSave = selectedZone && !saved;

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-7xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Find on Diagram
            </h3>
            <p className="text-sm text-gray-500">
              Select a zone and part code from the diagram.
              {selectedZone && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  Zone: {selectedZone}
                </span>
              )}
              {clickedPart && (
                <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                  Part: {clickedPart}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {canSave && (
              <button
                type="button"
                onClick={handleSave}
                className="text-sm px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700"
              >
                {clickedPart ? "Save Zone & Part" : "Save Zone"}
              </button>
            )}
            {saved && (
              <span className="text-sm text-green-600 font-medium">Saved</span>
            )}
            {selectedZone && (
              <button
                type="button"
                onClick={handleBack}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Back to Zones
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm px-3 py-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Model Code
          </label>
          <input
            type="text"
            value={model}
            onChange={(e) => handleModelChange(e.target.value)}
            className="block w-48 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="e.g. CI00601"
          />
        </div>

        <div className="border rounded-lg overflow-hidden bg-white min-h-[450px]">
          {!selectedZone && (
            <p className="text-sm text-gray-500 px-4 pt-4">
              Select a zone on the vehicle to view parts.
            </p>
          )}
          {open && (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title="GT Motive Part Diagrams"
              className="w-full border-0"
              style={{ height: 500 }}
              allow="cross-origin-isolated"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
